import { create } from 'zustand';
import { OrderService } from '../services/index.js';

export const useOrdersStore = create((set, get) => ({
  orders: [],
  activeOrders: [],
  processingOrders: [],
  completedOrders: [],
  filteredOrders: [],
  selectedDate: new Date(),
  isLoading: false,

  setSelectedDate: (date) => {
    const { orders } = get();
    const filteredOrders = orders.filter(order => {
      const orderDate = new Date(order.CreatedAt);
      return orderDate.toDateString() === date.toDateString();
    });

    set({
      selectedDate: date,
      filteredOrders,
    });
  },

  /**
   * Update an order's status
   */
  updateOrderStatus: async (orderId, status) => {
    set({ isLoading: true });
    try {
      const success = await OrderService.updateOrderStatus(orderId, status);

      if (success) {
        // Update local state
        const { orders, filteredOrders } = get();
        const updatedOrders = orders.map(order =>
          order.OrderID === orderId ? { ...order, Status: status, status } : order
        );
        const updatedFilteredOrders = filteredOrders.map(order =>
          order.OrderID === orderId ? { ...order, Status: status, status } : order
        );

        set({
          orders: updatedOrders,
          filteredOrders: updatedFilteredOrders,
          isLoading: false,
        });
      } else {
        throw new Error('Failed to update order status');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      set({ isLoading: false });
      throw error;
    }
  },

  /**
   * Fetch orders by status from the API
   */
  fetchOrders: async (outletId, status) => {
    set({ isLoading: true });
    try {
      const orders = await OrderService.fetchOrders(outletId, status);
      const { selectedDate } = get();

      // Filter orders by selected date
      const filteredOrders = orders.filter(order => {
        const orderDate = new Date(order.CreatedAt);
        return orderDate.toDateString() === selectedDate.toDateString();
      });

      set({
        orders,
        filteredOrders,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error fetching orders:', error);
      set({ isLoading: false });
      throw error;
    }
  },

  /**
   * Fetch completed orders
   */
  fetchCompleteOrders: async (outletId) => {
    set({ isLoading: true });
    try {
      const orders = await OrderService.fetchCompleteOrders(outletId);
      const { selectedDate } = get();

      // Filter orders by selected date
      const filteredOrders = orders.filter(order => {
        const orderDate = new Date(order.CreatedAt);
        return orderDate.toDateString() === selectedDate.toDateString();
      });

      set({
        orders,
        filteredOrders,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error fetching complete orders:', error);
      set({ isLoading: false });
      throw error;
    }
  },

  /**
   * Fetch active orders for POS display
   */
  fetchActiveOrders: async (outletId) => {
    set({ isLoading: true });
    try {
      const activeOrders = await OrderService.fetchOrders(outletId, 'Active');
      set({ activeOrders, isLoading: false });
    } catch (error) {
      console.error('Error fetching active orders:', error);
      set({ isLoading: false });
      throw error;
    }
  },

  /**
   * Fetch processing orders
   */
  fetchProcessingOrders: async (outletId) => {
    set({ isLoading: true });
    try {
      const processingOrders = await OrderService.fetchOrders(outletId, 'Processing');
      set({ processingOrders, isLoading: false });
    } catch (error) {
      console.error('Error fetching processing orders:', error);
      set({ isLoading: false });
      throw error;
    }
  },
}));
